'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  IconButton,
  ConfirmationDialog,
  Tooltip,
  Scroll,
  CopilotComposer,
  HeadingSection,
  BodyDefault,
  LabelDefault,
  Spinner,
} from '@terra/ui-design-system';
import { Message01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import type { CommentEntityType } from '@terra/shared-contracts-comments';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { MainContentTab } from '../../store/layoutStore';
import { useTargetType } from '../../store/projectStore';
import { getMainContentTabIcon } from '../layout/tabIcons';
import { getAttackSurfaceIcon } from '../layout/target-selector/attackSurfaces';
import { CommentDisplay } from './CommentDisplay';
import { CollaborationEmptyIllustration } from './CollaborationEmptyIllustration';

type CollaborationPanelProps = {
  entityType: CommentEntityType;
  entityId: string | undefined;
  entityName?: string;
  tabType?: MainContentTab;
  onMinimize?: () => void;
};

function getCollaborationEntityIcon({
  entityType,
  tabType,
  targetType,
}: {
  entityType: CommentEntityType;
  tabType: MainContentTab;
  targetType: TargetTypeType;
}) {
  if (entityType === 'APPLICATION') {
    const SurfaceIcon = getAttackSurfaceIcon(targetType);

    return <SurfaceIcon size={16} />;
  }

  return getMainContentTabIcon(tabType);
}

export function CollaborationPanel({
  entityType,
  entityId,
  entityName,
  tabType = 'application',
  onMinimize,
}: CollaborationPanelProps) {
  const t = useTranslations('layout');
  const tCommon = useTranslations('common');
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { userId: currentUserId } = useCurrentUser();
  const targetType = useTargetType();
  const entityIcon = getCollaborationEntityIcon({
    entityType,
    tabType,
    targetType,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['comments', entityType, entityId],
    queryFn: async () => {
      if (!entityId) return [];
      const response = await terraProxyApiClient.getComments(
        entityType,
        entityId,
      );

      return response?.comments || [];
    },
    enabled: !!entityId,
  });
  const createCommentMutation = useMutation({
    mutationFn: async ({ content: commentContent }: { content: string }) => {
      if (!entityId) throw new Error('No entity selected');

      return terraProxyApiClient.createComment({
        entityType,
        entityId,
        content: commentContent,
        parentCommentId: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['comments', entityType, entityId],
      });
    },
  });
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      return terraProxyApiClient.deleteComment(commentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['comments', entityType, entityId],
      });
    },
  });
  const handleDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
  };

  const handleConfirmDelete = () => {
    if (commentToDelete) {
      deleteCommentMutation.mutate(commentToDelete, {
        onSuccess: () => {
          setCommentToDelete(null);
        },
      });
    }
  };

  const handleSubmit = (message: { content: string }) => {
    if (!message.content.trim()) return;
    createCommentMutation.mutate({ content: message.content.trim() });
  };

  const comments = data || [];
  const showLoadingComments =
    Boolean(entityId) && isLoading && data === undefined;
  const showEmptyComments =
    Boolean(entityId) && !showLoadingComments && comments.length === 0;
  const body = !entityId ? (
    <VStack
      flex="1"
      w="full"
      alignItems="center"
      justifyContent="center"
      gap="2.5"
      p="4"
      minH="0"
    >
      <CollaborationEmptyIllustration />
      <HeadingSection color="fg.neutral.muted" textAlign="center">
        {t('selectItemToCollaborate')}
      </HeadingSection>
    </VStack>
  ) : showLoadingComments ? (
    <VStack
      flex="1"
      w="full"
      alignItems="center"
      justifyContent="center"
      p="4"
      minH="0"
    >
      <Spinner size="md" label={t('bottomRibbonStatsLoading')} />
    </VStack>
  ) : showEmptyComments ? (
    <VStack
      flex="1"
      w="full"
      alignItems="center"
      justifyContent="center"
      gap="2.5"
      p="4"
      minH="0"
    >
      <CollaborationEmptyIllustration />
      <VStack gap="2" alignItems="center" w="full" textAlign="center">
        <HeadingSection color="fg.neutral.muted">
          {t('noCommentsYet')}
        </HeadingSection>
        <BodyDefault color="fg.neutral.muted">
          {t('startByAddingComment')}
        </BodyDefault>
      </VStack>
    </VStack>
  ) : (
    <Box flex="1" overflow="hidden" position="relative" minH="0" py="6">
      <Scroll orientation="vertical" showScrollbar h="full">
        <VStack gap="1" px="2" w="full" alignItems="stretch">
          {comments.map((comment) => {
            const canDelete =
              currentUserId && comment.createdBy === currentUserId;

            return (
              <CommentDisplay
                key={comment.id}
                comment={comment}
                onDelete={() => handleDeleteComment(comment.id)}
                canDelete={!!canDelete}
                variant="inline"
              />
            );
          })}
        </VStack>
      </Scroll>
    </Box>
  );

  return (
    <Box
      h="full"
      w="full"
      bg="bg.neutral.light"
      display="flex"
      flexDirection="column"
      gap="0.5"
      pt="1"
      px="3"
      pb="3"
    >
      <Box
        h="8"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="4"
        w="full"
        flexShrink={0}
      >
        <HStack gap="1" alignItems="center" flex="1" minW="0">
          <Box
            flexShrink={0}
            display="flex"
            alignItems="center"
            color="fg.neutral.muted"
          >
            <Message01Icon size={16} />
          </Box>
          <LabelDefault color="fg.neutral.muted" truncate>
            {t('collaboration')}
          </LabelDefault>
        </HStack>
        {onMinimize ? (
          <Tooltip
            title={t('closeCollaboration')}
            positioning={{ placement: 'bottom' }}
          >
            <IconButton
              variant="ghost"
              size="xs"
              onClick={onMinimize}
              aria-label={t('closeCollaboration')}
              flexShrink={0}
              _hover={{
                color: 'fg.neutral.default',
                bg: 'transparent',
              }}
            >
              <Cancel01Icon size={16} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {entityName ? (
        <HStack
          w="full"
          alignItems="center"
          justifyContent="flex-start"
          flexShrink={0}
        >
          <HStack
            gap="1"
            alignItems="center"
            justifyContent="flex-start"
            px="2"
            py="1"
            borderRadius="l2"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.neutral.subtle"
            bg="bg.neutral.muted"
            minW="0"
            maxW="full"
          >
            <Box
              flexShrink={0}
              display="flex"
              alignItems="center"
              color="fg.neutral.subtle"
            >
              {entityIcon}
            </Box>
            <LabelDefault
              color="fg.neutral.subtle"
              truncate
              textAlign="left"
              flex="1"
              minW="0"
            >
              {entityName}
            </LabelDefault>
          </HStack>
        </HStack>
      ) : null}

      <Box w="full" h="px" bg="border.neutral.subtle" my="1" flexShrink={0} />

      {body}

      {entityId ? (
        <VStack
          gap={0}
          alignItems="stretch"
          bg="bg.default"
          minW={0}
          minH={0}
          flexShrink={0}
          w="full"
        >
          <CopilotComposer
            onSubmit={handleSubmit}
            onStop={() => undefined}
            isLoading={createCommentMutation.isPending}
            placeholder={t('writeAComment')}
          />
        </VStack>
      ) : null}

      <ConfirmationDialog
        isOpen={commentToDelete !== null}
        onOpenChange={({ open }) => {
          if (!open) {
            setCommentToDelete(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        loading={deleteCommentMutation.isPending}
        variant="destructive"
        title={t('deleteCommentTitle')}
        description={t('deleteCommentDescription')}
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
      />
    </Box>
  );
}
